import { useTranslation } from 'react-i18next';
import { Input } from '@/components/Input';

interface ZipFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** A message from the backend's 400. Null while nothing has been rejected. */
  error: string | null;
  disabled?: boolean;
}

/**
 * The ZIP capture on the residency gate, shown only when the lead has no ZIP
 * stored yet (`StatusOutput.zip_code`).
 *
 * There is no client-side error state: the Continue button stays disabled
 * until five digits are present, so the only rejection a candidate can see is
 * the backend's own — which keeps this screen from disagreeing with the
 * server about what a valid ZIP is.
 */
export default function ZipField({
  value,
  onChange,
  error,
  disabled,
}: ZipFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="mt-[22px] border-t border-[#e8e9ec] pt-[18px] text-left">
      <label
        htmlFor="zip_code"
        className="text-xs font-bold uppercase tracking-[0.3px] text-[#8a8f98]"
      >
        {t('residency.zip_label')}
      </label>
      <Input
        id="zip_code"
        kind="zipcode"
        autoComplete="postal-code"
        placeholder="00000"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={!!error}
        aria-describedby={error ? 'zip_code-error' : undefined}
        className={`mt-[7px] h-[52px] rounded-[14px] bg-white text-base font-semibold tracking-[3px] text-[#16171a] ${
          error ? 'border-[#d5584b]' : 'border-[#dfe1e6]'
        }`}
      />
      {error && (
        <p
          id="zip_code-error"
          role="alert"
          className="mt-[9px] text-[13px] font-semibold text-[#c0392b]"
        >
          {error}
        </p>
      )}
    </div>
  );
}
